import React, { useCallback, useMemo, useState } from 'react';
import {
  Field,
  Textarea,
  SpinButton,
  Text,
  Tooltip,
  mergeClasses,
} from '@fluentui/react-components';
import { DismissCircleRegular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { BasicButton } from '../BasicButton';
import { TextInput } from '../TextInput/TextInput';
import { DatePicker } from '../DatePicker/DatePicker';
import { TimePicker } from '../TimePicker/TimePicker';
import { ComboBox } from '../ComboBox/ComboBox';
import { RadioGroup } from '../RadioGroup/RadioGroup';
import { Checkbox } from '../Checkbox/Checkbox';
import { iconSize } from '@/theme';
import { PeoplePicker } from '../PeoplePicker';
import { evaluateVisibility } from '@utils/formValidation';
import { formatLocalDateForStorage, parseStoredDateToLocalDate } from '@utils/timeZone';
import { useFormFieldStyles } from './FormField.styles';
import type { FormFieldProps } from '@store/models/booking';

const MAX_ONSITE_CONTACTS = 10;

export const FormField: React.FC<FormFieldProps> = ({
  field,
  value,
  onChange,
  error,
  formData,
}) => {
  const { t } = useTranslation('bookingForm');
  const styles = useFormFieldStyles();
  const [showTooltip, setShowTooltip] = useState(false);

  const handleChange = useCallback((v: unknown) => onChange(field.key, v), [field.key, onChange]);

  const isVisible = useMemo(() => {
    if (!field.visibleWhen) return true;
    return evaluateVisibility(field.visibleWhen, formData);
  }, [field.visibleWhen, formData]);

  if (!isVisible) return null;

  const label = t(field.labelKey);
  const placeholder = field.placeholderKey ? t(field.placeholderKey) : '';
  const isRequired = field.validation?.some((r) => r.type === 'required') ?? false;
  const validationState = error ? ('error' as const) : ('none' as const);
  const fieldRootClassName = mergeClasses(
    styles.root,
    field.key === 'numberOfAttendees' ? styles.matchDateInputWidth : undefined
  );

  switch (field.type) {
    case 'text':
      return (
        <div className={fieldRootClassName}>
          <TextInput
            label={label}
            required={isRequired}
            placeholder={placeholder}
            value={(value as string) ?? ''}
            onChange={(e) => handleChange(e.target.value)}
            validationMessage={error ?? ''}
            validationState={validationState}
            disabled={field.disabled ?? false}
          />
          {field.helpLink && (
            <div style={{ position: 'relative' }}>
              <span
                className={styles.helpLink}
                role="button"
                tabIndex={0}
                onClick={() => setShowTooltip(!showTooltip)}
                onKeyDown={(e) => e.key === 'Enter' && setShowTooltip(!showTooltip)}
              >
                {t(field.helpLink)}
              </span>
              {showTooltip && field.helpTooltipKey && (
                <div className={styles.helpTooltip}>{t(field.helpTooltipKey)}</div>
              )}
            </div>
          )}
        </div>
      );

    case 'textarea':
      return (
        <div className={fieldRootClassName}>
          <Field
            label={label}
            required={isRequired}
            validationMessage={error ?? null}
            validationState={validationState}
          >
            <Textarea
              appearance="outline"
              placeholder={placeholder}
              value={(value as string) ?? ''}
              onChange={(_, data) => handleChange(data.value)}
              disabled={field.disabled ?? false}
              resize="vertical"
              rows={4}
            />
          </Field>
        </div>
      );

    case 'dropdown':
    case 'combobox': {
      const options = (field.options ?? []).map((opt) => ({
        key: opt.value,
        text: t(opt.labelKey),
        disabled: opt.disabled ?? false,
      }));
      const selectedKey = value != null && typeof value === 'string' ? value : '';
      const selectedText = options.find((o) => o.key === selectedKey)?.text ?? '';
      return (
        <div className={fieldRootClassName}>
          <ComboBox
            label={label}
            required={isRequired}
            placeholder={placeholder}
            options={options}
            selectedOptions={selectedKey ? [selectedKey] : []}
            value={selectedText}
            onOptionSelect={(_, data) => handleChange(data.optionValue ?? '')}
            validationMessage={error ?? ''}
            validationState={validationState}
            disabled={field.disabled ?? false}
          />
        </div>
      );
    }

    case 'datepicker':
      return (
        <div className={fieldRootClassName}>
          <DatePicker
            label={label}
            required={isRequired}
            placeholder={placeholder}
            value={parseStoredDateToLocalDate(value as string | null | undefined)}
            onSelectDate={(date) => handleChange(formatLocalDateForStorage(date))}
            validationMessage={error ?? ''}
            validationState={validationState}
            disabled={field.disabled ?? false}
          />
        </div>
      );

    case 'daterange': {
      const range = (value ?? { start: null, end: null }) as {
        start: string | null;
        end: string | null;
      };
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const startMinDate = today;
      const endMinDate = range.start ? new Date(range.start) : today;
      const startPlaceholder = t('bookingForm.fields.startDate.placeholder');
      const endPlaceholder = t('bookingForm.fields.endDate.placeholder');
      // Inclusive day count (Mon-Mon = 1 day, Mon-Tue = 2 days, etc.).
      const startDate = parseStoredDateToLocalDate(range.start);
      const endDate = parseStoredDateToLocalDate(range.end);
      const dayCount =
        startDate && endDate && endDate.getTime() >= startDate.getTime()
          ? Math.round((endDate.getTime() - startDate.getTime()) / 86_400_000) + 1
          : null;
      const dayHint =
        dayCount !== null ? t('bookingForm.fields.dateRange.dayCount', { count: dayCount }) : null;
      return (
        <div className={fieldRootClassName}>
          {dayHint && <span className={styles.fieldHint}>{dayHint}</span>}
          <Field
            label={label}
            required={isRequired}
            validationMessage={error ?? null}
            validationState={validationState}
          >
            <div className={styles.timeRangeRow}>
              <DatePicker
                placeholder={startPlaceholder}
                value={parseStoredDateToLocalDate(range.start)}
                onSelectDate={(d) => {
                  const newStart = formatLocalDateForStorage(d);
                  const shouldClearEnd =
                    newStart &&
                    range.end &&
                    parseStoredDateToLocalDate(range.end)! < parseStoredDateToLocalDate(newStart)!;
                  handleChange({ start: newStart, end: shouldClearEnd ? null : range.end });
                }}
                disabled={field.disabled ?? false}
                minDate={startMinDate}
                validationState={validationState}
                aria-required={isRequired}
                input={{ 'aria-label': `${label} start`, required: isRequired }}
              />
              <Text className={styles.separator} aria-hidden="true">
                -
              </Text>
              <DatePicker
                placeholder={endPlaceholder}
                value={parseStoredDateToLocalDate(range.end)}
                onSelectDate={(d) => handleChange({ ...range, end: formatLocalDateForStorage(d) })}
                disabled={field.disabled ?? false}
                minDate={endMinDate}
                validationState={validationState}
                aria-required={isRequired}
                input={{ 'aria-label': `${label} end`, required: isRequired }}
              />
            </div>
          </Field>
        </div>
      );
    }

    case 'timerange': {
      const tr = (value ?? { start: '', end: '' }) as { start: string; end: string };
      const startHourForEnd = tr.start ? parseInt(tr.start.split(':')[0] ?? '0', 10) : 0;
      const startPlaceholder = t('bookingForm.fields.startTime.placeholder');
      const endPlaceholder = t('bookingForm.fields.endTime.placeholder');
      // Fractional hours/day (e.g. 04:04 → 05:04 = 1; 09:30 → 11:00 = 1.5).
      const parseHHMM = (s: string): number | null => {
        const m = /^(\d{1,2}):(\d{2})$/.exec(s);
        if (!m) return null;
        const h = Number(m[1]);
        const min = Number(m[2]);
        return Number.isFinite(h) && Number.isFinite(min) ? h * 60 + min : null;
      };
      const startMin = parseHHMM(tr.start);
      const endMin = parseHHMM(tr.end);
      const hoursPerDay =
        startMin !== null && endMin !== null && endMin > startMin
          ? Math.round(((endMin - startMin) / 60) * 100) / 100
          : null;
      const timeHint =
        hoursPerDay !== null
          ? t('bookingForm.fields.timeRange.hoursPerDay', { count: hoursPerDay })
          : null;
      return (
        <div className={fieldRootClassName}>
          {timeHint && <span className={styles.fieldHint}>{timeHint}</span>}
          <Field
            label={label}
            required={isRequired}
            validationMessage={error ?? null}
            validationState={validationState}
          >
            <div className={styles.timeRangeRow}>
              <TimePicker
                placeholder={startPlaceholder}
                value={tr.start}
                input={{ 'aria-label': `${label} start`, required: isRequired }}
                aria-required={isRequired}
                onTimeStringChange={(newStart) => {
                  const shouldClearEnd = newStart && tr.end && tr.end <= newStart;
                  handleChange({ start: newStart, end: shouldClearEnd ? '' : tr.end });
                }}
                disabled={field.disabled ?? false}
                validationState={validationState}
              />
              <Text className={styles.separator} aria-hidden="true">
                -
              </Text>
              <TimePicker
                placeholder={endPlaceholder}
                value={tr.end}
                startHour={startHourForEnd as 0}
                input={{ 'aria-label': `${label} end`, required: isRequired }}
                aria-required={isRequired}
                onTimeStringChange={(newEnd) => handleChange({ ...tr, end: newEnd })}
                disabled={field.disabled ?? false}
                validationState={validationState}
              />
            </div>
          </Field>
        </div>
      );
    }

    case 'number': {
      const minRule = field.validation?.find((r) => r.type === 'min');
      const maxRule = field.validation?.find((r) => r.type === 'max');
      const minVal = typeof minRule?.value === 'number' ? minRule.value : undefined;
      const maxVal = typeof maxRule?.value === 'number' ? maxRule.value : undefined;
      return (
        <div
          className={fieldRootClassName}
          data-width-behavior={field.key === 'numberOfAttendees' ? 'match-date-input' : undefined}
        >
          <Field
            label={label}
            required={isRequired}
            validationMessage={error ?? null}
            validationState={validationState}
          >
            <Tooltip
              content={t('bookingForm.validation.maxValueTooltip', { max: maxVal })}
              relationship="description"
              visible={showTooltip}
            >
              <SpinButton
                className={
                  field.key === 'numberOfAttendees' ? styles.matchDateInputControl : undefined
                }
                appearance="outline"
                size="medium"
                value={typeof value === 'number' ? value : 0}
                onChange={(_, data) => {
                  const numValue =
                    data.value ?? (data.displayValue ? Number(data.displayValue) : undefined);
                  if (numValue !== undefined && !isNaN(numValue)) {
                    const clamped = maxVal !== undefined ? Math.min(numValue, maxVal) : numValue;
                    handleChange(clamped);
                  }
                }}
                input={{
                  maxLength: 4,
                  inputMode: 'numeric',
                  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
                    // Allow control keys
                    if (
                      e.key === 'Backspace' ||
                      e.key === 'Delete' ||
                      e.key === 'Tab' ||
                      e.key === 'ArrowLeft' ||
                      e.key === 'ArrowRight' ||
                      e.key === 'ArrowUp' ||
                      e.key === 'ArrowDown' ||
                      e.key === 'Home' ||
                      e.key === 'End'
                    ) {
                      setShowTooltip(false);
                      return;
                    }
                    // Block non-numeric characters
                    if (!/^\d$/.test(e.key)) {
                      e.preventDefault();
                      return;
                    }
                    // Prevent if resulting value would exceed max
                    if (maxVal !== undefined) {
                      const input = e.currentTarget;
                      const selStart = input.selectionStart ?? input.value.length;
                      const selEnd = input.selectionEnd ?? input.value.length;
                      const nextValue =
                        input.value.slice(0, selStart) + e.key + input.value.slice(selEnd);
                      if (Number(nextValue) > maxVal) {
                        e.preventDefault();
                        setShowTooltip(true);
                        setTimeout(() => setShowTooltip(false), 2000);
                      } else {
                        setShowTooltip(false);
                      }
                    }
                  },
                }}
                {...(minVal !== undefined && { min: minVal })}
                {...(maxVal !== undefined && { max: maxVal })}
                disabled={field.disabled ?? false}
              />
            </Tooltip>
          </Field>
        </div>
      );
    }

    case 'radio': {
      const radioOptions = (field.options ?? []).map((opt) => ({
        value: opt.value,
        label: t(opt.labelKey),
      }));
      return (
        <div className={fieldRootClassName}>
          <RadioGroup
            label={label}
            required={isRequired}
            options={radioOptions}
            value={(value as string) ?? (field.defaultValue as string) ?? ''}
            onChange={(_, data) => handleChange(data.value)}
            validationMessage={error ?? ''}
            validationState={validationState}
            disabled={field.disabled ?? false}
          />
        </div>
      );
    }

    case 'checkbox': {
      const checkboxLabel =
        field.linkUrl && field.linkText ? (
          <span>
            {label}{' '}
            <a
              href={field.linkUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.checkboxLink}
            >
              {t(field.linkText)}
            </a>
            .
          </span>
        ) : (
          label
        );
      const bannerClass =
        field.bannerVariant === 'salmon'
          ? styles.salmonBanner
          : field.linkUrl
            ? styles.policyBanner
            : styles.root;
      return (
        <div className={bannerClass}>
          <Checkbox
            label={checkboxLabel}
            required={isRequired}
            checked={!!value}
            onChange={(_, data) => handleChange(data.checked)}
            disabled={field.disabled ?? false}
          />
          {error && <Text className={styles.error}>{error}</Text>}
        </div>
      );
    }

    case 'contact-list': {
      type ContactEntry = { name: string; email: string };
      const contacts = (Array.isArray(value) ? value : [{ name: '', email: '' }]) as ContactEntry[];

      const updateContact = (idx: number, key: 'name' | 'email', val: string) => {
        const updated = contacts.map((c, i) => (i === idx ? { ...c, [key]: val } : c));
        handleChange(updated);
      };
      const addContact = () => {
        handleChange([...contacts, { name: '', email: '' }]);
      };
      const removeContact = (idx: number) => {        const updated = contacts.filter((_, i) => i !== idx);
        handleChange(updated.length > 0 ? updated : [{ name: '', email: '' }]);
      };

      return (
        <div className={fieldRootClassName}>
          {contacts.map((contact, idx) => (
            <div key={idx} className={styles.contactListRow}>
              <TextInput
                label={t('bookingForm.fields.contactName.label')}
                required={isRequired}
                placeholder={t('bookingForm.fields.contactName.placeholder')}
                value={contact.name}
                onChange={(e) => updateContact(idx, 'name', e.target.value)}
                autoComplete="name"
                validationState={error && !contact.name ? 'error' : 'none'}
                validationMessage={
                  error && !contact.name ? t('bookingForm.validation.required') : ''
                }
              />
              <TextInput
                label={t('bookingForm.fields.contactEmail.label')}
                required={isRequired}
                placeholder={t('bookingForm.fields.contactEmail.placeholder')}
                value={contact.email}
                onChange={(e) => updateContact(idx, 'email', e.target.value)}
                type="email"
                autoComplete="email"
                validationState={error && !contact.email ? 'error' : 'none'}
                validationMessage={
                  error && !contact.email ? t('bookingForm.validation.required') : ''
                }
              />
              {contacts.length > 1 && (
                <DismissCircleRegular
                  className={styles.contactDeleteBtn}
                  style={{ fontSize: iconSize(20) }}
                  onClick={() => removeContact(idx)}
                  role="button"
                  tabIndex={0}
                  aria-label={t('bookingForm.buttons.removeContact')}
                />
              )}
            </div>
          ))}
          <BasicButton
            variant="primary"
            size="small"
            className={styles.addContactBtn}
            disabled={contacts.length >= MAX_ONSITE_CONTACTS}
            onClick={addContact}
          >
            {t('bookingForm.buttons.addMoreContacts')}
          </BasicButton>
          {error && <Text className={styles.error}>{error}</Text>}
        </div>
      );
    }

    case 'people-picker': {
      return (
        <div className={fieldRootClassName}>
          <PeoplePicker
            value={value}
            onChange={onChange}
            error={error}
            disabled={field.disabled ?? false}
            label={label}
            placeholder={placeholder}
            required={isRequired}
          />
        </div>
      );
    }

    /* c8 ignore start */
    default: {
      const _exhaustive: never = field.type;
      console.warn(
        `[FormField] Unrecognized field type "${_exhaustive as string}" for key "${field.key}". Skipping.`
      );
      return null;
    }
  }
};

FormField.displayName = 'FormField';
